import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Copy,
    Inbox,
    AlertCircle,
    CheckCircle,
    X,
    Clock,
    Wallet,
    ArrowUpRight,
    ExternalLink,
    Check,
    Ban,
    ChevronDown,
    ChevronUp,
    Shield,
    DollarSign,
} from 'lucide-react';
import { API_BASE_URL } from '../../utils/apiConfig';
import { useTranslation } from 'react-i18next';
import { fetchUserWallet, selectWallets } from '../../store/slices/walletSlice';

const WidthdrawalNew = () => {
    const dispatch = useDispatch();
    const walletData = useSelector(selectWallets);
    const { t } = useTranslation();

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1, limit: 20, total: 0, totalPages: 0
    });
    const [page, setPage] = useState(1);
    const limit = 20;
    const [error, setError] = useState('');

    // Review modal state
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateError, setUpdateError] = useState('');
    const [updateSuccess, setUpdateSuccess] = useState('');
    const [showNotes, setShowNotes] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [confirmAction, setConfirmAction] = useState(null); // 'approve' | 'reject' | null
    const [copiedField, setCopiedField] = useState(null);

    // Fetch wallet balances on mount
    useEffect(() => {
        dispatch(fetchUserWallet());
    }, [dispatch]);

    // Always fetch pending withdrawals only
    const fetchTransactions = async (pageNum = 1) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: pageNum.toString(),
                limit: limit.toString(),
                status: 'pending',
            });
            const response = await fetch(
                `${API_BASE_URL}/withdrawal/my-withdrawals?${params.toString()}`,
                { credentials: "include", headers: { "Content-Type": "application/json" } }
            );
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            if (data.success) {
                setTransactions(data.data || []);
                setPagination({ page: data.page || 1, limit, total: data.total || 0, totalPages: data.totalPages || 0 });
            } else {
                throw new Error(data.error || 'Failed to fetch withdrawals');
            }
        } catch (err) {
            console.error('Error fetching withdrawals:', err);
            setError(err.message);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTransactions(1); }, []);

    // Auto-poll every 30 seconds to show new requests instantly
    useEffect(() => {
        const interval = setInterval(() => {
            if (!showReviewModal) {
                fetchTransactions(page);
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [page, showReviewModal]);

    // Get merchant balance for a specific currency
    const getBalanceForCurrency = (currencyType) => {
        if (!walletData || !Array.isArray(walletData)) return 0;
        const wallet = walletData.find(w => w.walletNetwork === currencyType);
        return wallet?.balance || 0;
    };

    // Check if merchant has sufficient balance for a withdrawal
    const hasSufficientBalance = (transaction) => {
        const balance = getBalanceForCurrency(transaction.currencyType);
        return balance >= (transaction.amount || 0);
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text).catch(err => console.error('Failed to copy:', err));
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const truncateAddress = (address, start = 6, end = 4) => {
        if (!address) return '';
        if (address.length <= start + end) return address;
        return `${address.substring(0, start)}...${address.substring(address.length - end)}`;
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchTransactions(newPage);
    };

    const getExplorerUrl = (currencyType, address) => {
        if (currencyType?.includes('TRC20')) return `https://tronscan.org/#/address/${address}`;
        return `https://etherscan.io/address/${address}`;
    };

    // Open review modal
    const handleOpenReview = (transaction) => {
        setSelectedTransaction(transaction);
        setUpdateError('');
        setUpdateSuccess('');
        setAdminNotes('');
        setShowNotes(false);
        setConfirmAction(null);
        setShowReviewModal(true);
    };

    const handleCloseReview = () => {
        setShowReviewModal(false);
        setSelectedTransaction(null);
        setUpdateError('');
        setUpdateSuccess('');
        setConfirmAction(null);
        setAdminNotes('');
    };

    // Execute approve or reject
    const handleAction = async (action) => {
        if (!selectedTransaction?._id) return;
        setUpdateLoading(true);
        setUpdateError('');
        try {
            const response = await fetch(
                `${API_BASE_URL}/withdrawal/${selectedTransaction._id}`,
                {
                    method: 'PATCH', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action,
                        adminNotes: adminNotes || `${action === 'approve' ? 'Approved' : 'Rejected'} via merchant dashboard`,
                    }),
                }
            );
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || `Failed to ${action} withdrawal`);
            setUpdateSuccess(action === 'approve' ? t('withdrawals.approvedSuccess') : t('withdrawals.rejectedSuccess'));
            setTimeout(() => {
                handleCloseReview();
                fetchTransactions(page);
            }, 1500);
        } catch (err) {
            setUpdateError(err.message);
            setConfirmAction(null);
        } finally {
            setUpdateLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
        });
    };

    const userRole = localStorage.getItem("userRole");
    const canAct = userRole !== "operation";

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">{t("withdrawals.title")}</h1>
                        <p className="text-sm text-gray-500 mt-1">{t("withdrawals.subtitle")}</p>
                    </div>
                    {pagination.total > 0 && (
                        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-gray-200">
                            <Clock className="w-4 h-4 text-amber-500" />
                            <span className="text-sm font-medium text-gray-700">{pagination.total} {t("withdrawals.pending")}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-[25px] shadow-sm overflow-hidden">
                {loading ? (
                    <div className="overflow-x-auto p-2">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-white">
                                <tr>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.dateRequested")}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.amount")}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.sufficientBalance")}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.action") || "Action"}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {[...Array(5)].map((_, i) => (
                                    <tr key={`skeleton-${i}`} className="animate-pulse">
                                        <td className="px-6 py-5"><span className="inline-block h-4 bg-gray-200 rounded w-36"></span></td>
                                        <td className="px-6 py-5"><span className="inline-block h-4 bg-gray-200 rounded w-28"></span></td>
                                        <td className="px-6 py-5"><span className="inline-block h-6 bg-gray-200 rounded-full w-16"></span></td>
                                        <td className="px-6 py-5"><span className="inline-block h-9 bg-gray-200 rounded-lg w-20"></span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
                        <div className="flex items-center">
                            <AlertCircle className="w-4 h-4 text-red-400 mr-2" />
                            <span className="text-red-700 text-sm">{error}</span>
                        </div>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle className="w-8 h-8 text-green-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">{t("withdrawals.allCaughtUp")}</h3>
                        <p className="text-sm text-gray-500 max-w-sm">{t("withdrawals.noPendingMessage")}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto p-2">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-white">
                                <tr>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.dateRequested")}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.amount")}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.sufficientBalance")}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">{t("withdrawals.action") || "Action"}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {transactions.map((transaction, index) => {
                                    const sufficient = hasSufficientBalance(transaction);
                                    return (
                                        <tr key={transaction._id || index} className="hover:bg-gray-50 transition-colors">
                                            {/* Date Requested */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {formatDate(transaction.createdAt)}
                                                </div>
                                            </td>

                                            {/* Amount + Currency */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        ${transaction.amount?.toLocaleString() || '0'}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                        <img
                                                            src={transaction.currencyType === "USDT-TRC20" ? '/TRC20.png' : '/USDT-ERC20.png'}
                                                            alt="chain"
                                                            className="w-4 h-4"
                                                        />
                                                        {transaction.currencyType?.replace('USDT-', '') || 'TRC20'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Sufficient Balance */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {sufficient ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                                        <Check className="w-3 h-3" />
                                                        {t("withdrawals.yes")}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                                                        <X className="w-3 h-3" />
                                                        {t("withdrawals.no")}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Action */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {canAct && (
                                                    <button
                                                        onClick={() => handleOpenReview(transaction)}
                                                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 hover:shadow-md active:scale-95"
                                                        style={{ background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)" }}
                                                    >
                                                        {t("withdrawals.review")}
                                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.total > 0 && pagination.totalPages > 1 && (
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-sm text-gray-500">
                        {t("withdrawals.showingResults")} {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                            {t("common.previous") || "Previous"}
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-500">
                            {pagination.page} / {pagination.totalPages}
                        </span>
                        <button onClick={() => handlePageChange(page + 1)} disabled={page >= pagination.totalPages} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                            {t("common.next") || "Next"}
                        </button>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            {showReviewModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) handleCloseReview(); }}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-auto overflow-hidden" onClick={(e) => e.stopPropagation()}>

                        {/* Success state */}
                        {updateSuccess ? (
                            <div className="p-8 text-center">
                                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-green-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">{updateSuccess}</h3>
                                <p className="text-sm text-gray-500">{t("withdrawals.closingAutomatically")}</p>
                            </div>
                        ) : (
                            <>
                                {/* Header */}
                                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                                                <Shield className="w-5 h-5 text-amber-600" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-900">{t("withdrawals.reviewRequest")}</h3>
                                                <p className="text-xs text-gray-500">{t("withdrawals.reviewSubtitle")}</p>
                                            </div>
                                        </div>
                                        <button onClick={handleCloseReview} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                            <X className="w-5 h-5 text-gray-400" />
                                        </button>
                                    </div>
                                </div>

                                {/* Withdrawal details */}
                                <div className="px-6 py-4 space-y-4">
                                    {/* Amount card */}
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm text-gray-500">{t("withdrawals.requestedAmount")}</span>
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white text-gray-600 border border-gray-200">
                                                <img
                                                    src={selectedTransaction.currencyType === "USDT-TRC20" ? '/TRC20.png' : '/USDT-ERC20.png'}
                                                    alt="chain"
                                                    className="w-4 h-4"
                                                />
                                                {selectedTransaction.currencyType?.replace('USDT-', '') || 'TRC20'}
                                            </span>
                                        </div>
                                        <div className="text-3xl font-bold text-gray-900">
                                            ${selectedTransaction.amount?.toLocaleString() || '0'}
                                            <span className="text-base font-normal text-gray-400 ml-1">USDT</span>
                                        </div>

                                        {/* Balance check */}
                                        {(() => {
                                            const balance = getBalanceForCurrency(selectedTransaction.currencyType);
                                            const sufficient = balance >= (selectedTransaction.amount || 0);
                                            return (
                                                <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg ${sufficient ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                                    {sufficient ? (
                                                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                    ) : (
                                                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                                    )}
                                                    <span className={`text-xs font-medium ${sufficient ? 'text-green-700' : 'text-red-700'}`}>
                                                        {t("withdrawals.yourBalance")}: ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {selectedTransaction.currencyType?.replace('USDT-', '')}
                                                        {sufficient ? ` — ${t("withdrawals.sufficientFunds")}` : ` — ${t("withdrawals.insufficientFunds")}`}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* Details grid */}
                                    <div className="space-y-3">
                                        {/* Destination */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500">{t("withdrawals.destination")}</span>
                                            <div className="flex items-center gap-2">
                                                <code className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
                                                    {truncateAddress(selectedTransaction.clientWallet, 8, 6)}
                                                </code>
                                                <button
                                                    onClick={() => copyToClipboard(selectedTransaction.clientWallet, 'wallet')}
                                                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                    title={t("withdrawals.copyAddress")}
                                                >
                                                    {copiedField === 'wallet' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                                <a
                                                    href={getExplorerUrl(selectedTransaction.currencyType, selectedTransaction.clientWallet)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                    title={t("withdrawals.viewOnExplorer")}
                                                >
                                                    <ExternalLink className="w-4 h-4" />
                                                </a>
                                            </div>
                                        </div>

                                        {/* Invoice */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500">{t("withdrawals.invoiceId")}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-900">{truncateAddress(selectedTransaction.invoiceNo, 8, 6)}</span>
                                                <button
                                                    onClick={() => copyToClipboard(selectedTransaction.invoiceNo, 'invoice')}
                                                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    {copiedField === 'invoice' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Date */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-500">{t("withdrawals.dateRequested")}</span>
                                            <span className="text-sm font-medium text-gray-900">{formatDate(selectedTransaction.createdAt)}</span>
                                        </div>

                                        {/* Fee */}
                                        {selectedTransaction.fee > 0 && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-500">{t("withdrawals.fee")}</span>
                                                <span className="text-sm font-medium text-gray-900">${selectedTransaction.fee} USDT</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Optional notes */}
                                    <div>
                                        <button
                                            onClick={() => setShowNotes(!showNotes)}
                                            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                                        >
                                            {showNotes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            {t("withdrawals.addNotes")}
                                        </button>
                                        {showNotes && (
                                            <textarea
                                                value={adminNotes}
                                                onChange={(e) => setAdminNotes(e.target.value)}
                                                className="w-full mt-2 p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none"
                                                rows="3"
                                                placeholder={t('withdrawals.notesPlaceholder')}
                                            />
                                        )}
                                    </div>

                                    {/* Error */}
                                    {updateError && (
                                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                            <span className="text-red-700 text-sm flex-1">{updateError}</span>
                                            <button onClick={() => setUpdateError('')} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                                        </div>
                                    )}
                                </div>

                                {/* Action buttons */}
                                <div className="px-6 pb-6 pt-2">
                                    {confirmAction === null ? (
                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => setConfirmAction('reject')}
                                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-red-200 text-red-700 font-medium hover:bg-red-50 transition-all duration-200 active:scale-[0.98]"
                                            >
                                                <Ban className="w-4 h-4" />
                                                {t("withdrawals.reject")}
                                            </button>
                                            <button
                                                onClick={() => setConfirmAction('approve')}
                                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-medium transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
                                                style={{ background: "linear-gradient(180deg, #22c55e 0%, #16a34a 100%)" }}
                                            >
                                                <Check className="w-4 h-4" />
                                                {t("withdrawals.approve")}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className={`p-3 rounded-xl text-sm font-medium text-center ${confirmAction === 'approve' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                                {confirmAction === 'approve'
                                                    ? t("withdrawals.confirmApprove")
                                                    : t("withdrawals.confirmReject")}
                                            </div>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => setConfirmAction(null)}
                                                    disabled={updateLoading}
                                                    className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-all disabled:opacity-50"
                                                >
                                                    {t("common.cancel")}
                                                </button>
                                                <button
                                                    onClick={() => handleAction(confirmAction)}
                                                    disabled={updateLoading}
                                                    className={`flex-1 px-4 py-3 rounded-xl text-white font-medium transition-all disabled:opacity-50 ${confirmAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                                                >
                                                    {updateLoading ? (
                                                        <span className="flex items-center justify-center gap-2">
                                                            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                                                            {t("withdrawals.processing")}
                                                        </span>
                                                    ) : (
                                                        t("withdrawals.confirmAction")
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WidthdrawalNew;
